import { SiteFooter, SiteNav } from "@/components/site-chrome";
import { Badge, Card, LinkButton, SectionTitle } from "@/components/ui";
import { TemplateShowcase } from "@/components/template-showcase";
import { LocalMoney } from "@/components/currency-provider";

const STEPS = [
  { n: "01", title: "Choose your starting point", desc: "Pick a video style. Add your own photo, or start with a product link and details." },
  { n: "02", title: "Make it yours", desc: "Add your words and voice. For a talking video, adjust the person and background, then confirm the preview." },
  { n: "03", title: "Generate and keep", desc: "Check the price before generating. Find your finished videos, covers and durations in Projects." },
];

const PRICING = [
  {
    name: "Pay as you go",
    priceCents: 190,
    unit: "/video",
    desc: "No subscription and no monthly fee. Add funds and pay in USD.",
    features: [
      { cents: 190, suffix: " welcome balance on signup" },
      "Pay only for what you render",
      "Use your own photos and voice",
      "Preview the scene before generating",
      "Saved videos with playback and downloads",
    ],
    cta: "Start free",
    highlight: false,
  },
  {
    name: "Pay as you go Batch",
    priceCents: 120,
    unit: "/video",
    desc: "Volume pricing for teams that test at scale",
    features: [
      "Everything in Pay as you go",
      { cents: 160, suffix: "/video from 50 videos" },
      { cents: 120, suffix: "/video from 500 videos" },
      "Batch up to 50 variants per run",
      "Built for hook & avatar A/B testing",
    ],
    cta: "Add funds",
    highlight: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    unit: "",
    desc: "For agencies and high-volume teams",
    features: [
      "Negotiated per-video rate",
      "Custom avatar cloning",
      "Team seats & sub-accounts",
      "API access",
      "Dedicated success manager",
    ],
    cta: "Contact us",
    highlight: false,
  },
];


export default function Home() {
  return (
    <>
      <SiteNav />
      <section className="mx-auto w-full max-w-6xl px-5 pt-6 pb-12 sm:pt-8">
        <div className="mx-auto max-w-3xl text-center">
          <h1 className="mx-auto w-fit max-w-full bg-gradient-to-r from-white to-accent bg-clip-text text-transparent text-[clamp(2rem,8.5vw,2.65rem)] leading-[1.08] font-semibold tracking-[-0.035em] sm:text-[4.25rem]">
            <span className="block">Make your video</span>{" "}
            <span className="inline-flex flex-wrap items-baseline justify-center gap-x-2 sm:gap-x-3">
              <span>in seconds.</span>{" "}
              <span className="whitespace-nowrap text-[0.485em] font-normal tracking-normal text-muted">
                ... and more.
              </span>
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-md text-balance text-[15px] leading-6 text-muted sm:text-base">
            Turn your photos, words and voice into videos.
          </p>
        </div>
        <div id="styles" className="scroll-mt-24"><TemplateShowcase /></div>
      </section>

      <section id="how" className="mx-auto w-full max-w-6xl scroll-mt-24 px-5 py-12">
        <SectionTitle eyebrow="How it works" title="Your video, in three steps" />
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {STEPS.map(step => (
            <Card key={step.n} className="p-6">
              <div className="mb-3 font-mono text-2xl font-semibold text-purple-300">{step.n}</div>
              <h3 className="text-base font-semibold">{step.title}</h3>
              <p className="mt-2 text-sm leading-6 text-muted">{step.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      <section id="pricing" className="mx-auto w-full max-w-6xl scroll-mt-24 px-5 py-12">
        <SectionTitle eyebrow="Pricing" title="Pay as you go — no subscription" desc="See your price before generating. Payments are charged in USD." />
        <div className="mt-10 grid gap-5 lg:grid-cols-3">
          {PRICING.map(plan => (
            <Card key={plan.name} className={plan.highlight ? "relative border-[var(--accent)]/50 bg-gradient-to-b from-[var(--accent)]/12 to-transparent p-6" : "p-6"}>
              {plan.highlight && <Badge tone="accent" className="mb-3">Batch pricing</Badge>}
              <div className="text-sm font-medium">{plan.name}</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-3xl font-semibold tracking-tight">{"priceCents" in plan && plan.priceCents ? <LocalMoney usdCents={plan.priceCents} prefix="From " /> : plan.price}</span>
                <span className="text-sm text-muted">{plan.unit}</span>
              </div>
              <p className="mt-2 text-[13px] text-muted">{plan.desc}</p>
              <ul className="my-6 space-y-2">
                {plan.features.map(feature => (
                  <li key={typeof feature === "string" ? feature : `${feature.cents}-${feature.suffix}`} className="flex gap-2 text-[13px]">
                    <span className="text-[var(--success)]">✓</span>
                    <span className="text-muted">{typeof feature === "string" ? feature : <LocalMoney usdCents={feature.cents} suffix={feature.suffix} />}</span>
                  </li>
                ))}
              </ul>
              <LinkButton href={plan.cta === "Add funds" ? "/studio/billing" : plan.cta === "Contact us" ? "mailto:hello@easyshot.ai" : "/studio"} variant={plan.highlight ? "primary" : "secondary"} className="w-full">{plan.cta}</LinkButton>
            </Card>
          ))}
        </div>
      </section>
      <SiteFooter />
    </>
  );
}
